import React, { useEffect, useState } from 'react'
import { Box, Divider, Text, List, ListItem, Flex, Tooltip } from '@chakra-ui/core'
import styled from '@emotion/styled'
import colors from 'constants/colors'
import { ChevronLeft, LogOut, Tag, UsersRound, Workflow } from 'lucide-react'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { useNavigate } from 'react-router-dom'
import { isRtlSelector } from 'redux/selectors/general'
import useLocaleText from 'components/useLocaleText'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { DEVICE_TYPES } from 'constants/UI'

const SidebarContainer = styled(Box)<{ isCollapsed: boolean }>`
  width: ${(props) => (props.isCollapsed ? '4rem' : 'max-content')};
  max-width: 350px;
  padding-top: 15px;
  background-color: ${colors.greyMedium};
  height: calc(100vh - 94px);
  transition: width 0.3s ease;

  @media (max-width: 768px) {
    width: ${(props) => (props.isCollapsed ? '3rem' : 'max-content')};
  }
`

const NavItem = styled(ListItem)<{ isActive: boolean; isCollapsed: boolean; color: string }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  column-gap: 10px;
  color: ${(props) => (props.isActive ? props.color : colors.greyMain)};
  transition: all 0.2s;
  border-radius: 5px;
  padding: 0.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  background-color: ${(props) => (props.isActive ? colors.greyLight : 'transparent')};
  justify-content: ${(props) => (props.isCollapsed ? 'center' : 'flex-start')};

  &:hover {
    color: ${({ color }) => color};
    background-color: ${colors.greyLight};
  }
`

interface AdminSidebarProps {
  activeSection: string
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ activeSection }) => {
  const theme = useSelector((state: RootState) => state.general.theme)
  const { uid } = useSelector((state: RootState) => state.auth)
  const { windowWidth } = useWindowDimensions()
  const navigate = useNavigate()
  const isRtl = useSelector(isRtlSelector)
  const [isCollapsed, setIsCollapsed] = useState(true)

  const admin_console = useLocaleText('admin_console')
  const t_users = useLocaleText('users')
  const t_tags = useLocaleText('tags')
  const t_jobs = useLocaleText('jobs', true)
  const t_exit = useLocaleText('exit')

  // Check if the user can access the Jobs section
  const canAccessJobs = uid && uid.startsWith('sto-')

  const handleNavigation = (section: string) => {
    navigate(`/admin/${section}`)
  }

  useEffect(() => {
    if (DEVICE_TYPES.TABLET(windowWidth)) {
      setIsCollapsed(true)
    }
  }, [windowWidth])

  return (
    <SidebarContainer isCollapsed={isCollapsed}>
      <Flex
        flexDirection="row"
        p="0.5rem"
        display={{ base: 'none', md: 'flex' }}
        justifyContent={isCollapsed ? 'center' : 'space-between'}
        alignItems="center"
      >
        {!isCollapsed && (
          <Text fontSize="20px" fontWeight="bold">
            {admin_console}
          </Text>
        )}
        <NoFocusButton
          borderRadius="10px"
          minW="unset"
          minH="unset"
          p="5px"
          w="30px"
          h="30px"
          bg="transparent"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: colors.greyMedium2 }}
          onClick={() => setIsCollapsed(!isCollapsed)}
          transform={isCollapsed ? 'rotate(180deg)' : 'none'}
          transition="transform 0.3s ease"
        >
          <ChevronLeft style={{ transform: isRtl ? 'rotate(180deg)' : 'none' }} />
        </NoFocusButton>
      </Flex>
      <Divider my={0} />
      <Flex flexDirection="column" justifyContent="space-between" h="calc(100% - 40px)">
        <List p="0.5rem">
          <Tooltip
            label="Users"
            placement="bottom"
            hasArrow
            aria-label="Users"
            zIndex={10000}
            display={!isCollapsed ? 'none' : 'block'}
          >
            <NavItem
              isActive={activeSection === 'users'}
              onClick={() => handleNavigation('users')}
              isCollapsed={isCollapsed}
              color={theme?.elementsColor || colors.blueDark}
            >
              <UsersRound size={18} />
              {!isCollapsed && <Text>{t_users}</Text>}
            </NavItem>
          </Tooltip>
          <Tooltip
            label="Tags"
            placement="bottom"
            hasArrow
            aria-label="Tags"
            zIndex={10000}
            display={!isCollapsed ? 'none' : 'block'}
          >
            <NavItem
              color={theme?.elementsColor || colors.blueDark}
              isActive={activeSection === 'tags'}
              onClick={() => handleNavigation('tags')}
              isCollapsed={isCollapsed}
            >
              <Tag size={18} />
              {!isCollapsed && <Text>{t_tags}</Text>}
            </NavItem>
          </Tooltip>
          {canAccessJobs && (
            <Tooltip
              label="Jobs"
              placement="bottom"
              hasArrow
              aria-label="Jobs"
              zIndex={10000}
              display={!isCollapsed ? 'none' : 'block'}
            >
              <NavItem
                isActive={activeSection === 'jobs'}
                onClick={() => handleNavigation('jobs')}
                isCollapsed={isCollapsed}
                color={theme?.elementsColor || colors.blueDark}
              >
                <Workflow size={18} />
                {!isCollapsed && <Text>{t_jobs}</Text>}
              </NavItem>
            </Tooltip>
          )}
        </List>
        <Box p="0.5rem">
          <Divider />
          <Tooltip
            label="Logout"
            placement="bottom"
            hasArrow
            aria-label="Logout"
            zIndex={10000}
            display={!isCollapsed ? 'none' : 'block'}
          >
            <NavItem
              isActive={false}
              onClick={() => navigate('/')}
              isCollapsed={isCollapsed}
              color="red"
              _hover={{ color: 'red' }}
            >
              <LogOut size={18} color="red" />
              {!isCollapsed && <Text color="red">{t_exit}</Text>}
            </NavItem>
          </Tooltip>
        </Box>
      </Flex>
    </SidebarContainer>
  )
}
